import { ChangeDetectionStrategy, Component, computed, inject, Signal } from '@angular/core';
import {
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonHeader,
  IonIcon,
  IonRow,
  IonTitle,
  IonToolbar,
  LoadingController,
  ModalController,
} from '@ionic/angular/standalone';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SpinnerLoadingComponent } from '@okcargo-apps/layout-ionic';
import { EStopStatus, EStopType, getEmptyFreightDetailedForCarrier, ICoords } from '@okcargo-apps/shared-domain';
import { AnalyticsService } from '@okcargo-apps/shared-infrastructure-ionic';
import { FreightCarrierInprogressCardAddressComponent } from '../freight-carrier-inprogress-card-address/freight-carrier-inprogress-card-address.component';
import { FreightCarrierInprogressCardScheduleComponent } from '../freight-carrier-inprogress-card-schedule/freight-carrier-inprogress-card-schedule.component';
import { FreightCarrierMutationsService } from '../freight-carrier-mutations.service';
import { FreightCarrierStopGeolocationComponent } from '../freight-carrier-stop-geolocation/freight-carrier-stop-geolocation.component';
import { IFreightCarrierDetailedVM } from '../freight-carrier-store.model';

@Component({
  selector: 'lib-freight-carrier-stop-start-modal',
  templateUrl: './freight-carrier-stop-start-modal.component.html',
  styleUrls: ['./freight-carrier-stop-start-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IonRow,
    IonGrid,
    IonCol,
    IonFooter,
    IonIcon,
    IonHeader,
    IonContent,
    IonToolbar,
    IonButtons,
    IonButton,
    IonTitle,
    FreightCarrierInprogressCardAddressComponent,
    FreightCarrierInprogressCardScheduleComponent,
    SpinnerLoadingComponent,
    TranslateModule,
    FreightCarrierStopGeolocationComponent,
  ],
})
export class FreightCarrierStopStartModalComponent {
  private readonly modalCtrl = inject(ModalController);
  private readonly loadingCtrl = inject(LoadingController);
  private readonly translate = inject(TranslateService);
  private readonly freightCarrierMutationsService = inject(FreightCarrierMutationsService);
  private readonly analyticsService = inject(AnalyticsService);

  readonly EStopType = EStopType;

  // comes from the component that opens the modal
  public readonly freightVM!: Signal<IFreightCarrierDetailedVM>;

  protected readonly isLoading = computed(() => this.freightVM().isLoading);

  protected readonly freightDetail = computed(() => this.freightVM().item ?? getEmptyFreightDetailedForCarrier());

  // The detailed stop extends the basic one, so a single lookup covers display and command data.
  protected readonly nextStop = computed(() =>
    this.freightDetail().stopsDetailed.find((stop) => stop.id === this.freightDetail().nextStopId),
  );

  private coords: ICoords | null = null;

  public onCoordsChange(coords: ICoords | null): void {
    this.coords = coords;
  }

  public cancel(): Promise<boolean> {
    return this.modalCtrl.dismiss(null, 'cancel');
  }

  public async confirm(): Promise<boolean> {
    const loading = await this.loadingCtrl.create({
      message: this.translate.instant('general.send_data'),
    });
    loading.present();

    const nextStop = this.nextStop();
    if (nextStop !== undefined) {
      await this.freightCarrierMutationsService.modifyStopStatus(
        nextStop,
        this.freightDetail(),
        EStopStatus.STARTED,
        this.coords ?? undefined,
      );

      this.analyticsService.logEvent('stop_status_started');
    }

    await new Promise((resolve) => setTimeout(resolve, 1000));

    await loading.dismiss();

    return this.modalCtrl.dismiss(null, 'confirm');
  }
}
